// Copyright (C) 2020-2022 Intel Corporation
// Copyright (C) 2023-2024 CVAT.ai Corporation
//
// SPDX-License-Identifier: MIT

import React from 'react';
import { DeleteOutlined, EditOutlined, GroupOutlined } from '@ant-design/icons';
import Text from 'antd/lib/typography/Text';
import Popover from 'antd/lib/popover';

import CVATTooltip from 'components/common/cvat-tooltip';
import { computeTextColor } from 'utils/compute-text-color';
import config from 'config';
import { t } from 'utils/i18n';
import { LabelOptColor } from './common';

interface ConstructorViewerItemProps {
    label: LabelOptColor;
    color?: string;
    onUpdate: (label: LabelOptColor) => void;
    onDelete: (label: LabelOptColor) => void;
}

export default function ConstructorViewerItem(props: ConstructorViewerItemProps): JSX.Element {
    const {
        color, label, onUpdate, onDelete,
    } = props;

    const backgroundColor = color || config.NEW_LABEL_COLOR;
    const textColor = computeTextColor(backgroundColor);

    const sublabels =
        label.sublabels?.map((i) => (
            <ConstructorViewerItem onUpdate={onUpdate} onDelete={onDelete} label={i} key={i.id} color={i.color} />
        )) || [];

    return (
        <div style={{ background: backgroundColor }} className='cvat-constructor-viewer-item'>
            <Text style={{ color: textColor }}>{label.name}</Text>
            {!!sublabels?.length && (
                <Popover
                    placement='top'
                    title={`${label.name} sublabels`}
                    content={<div style={{ display: 'flex', flexWrap: 'wrap', maxWidth: 400 }}>{sublabels}</div>}
                    destroyTooltipOnHide={{ keepParent: false }}
                >
                    <span style={{ color: textColor, cursor: 'pointer' }} tabIndex={0}>
                        <GroupOutlined />
                    </span>
                </Popover>
            )}
            <CVATTooltip title={t('Update attributes')}>
                <span
                    style={{ color: textColor, cursor: 'pointer' }}
                    role='button'
                    tabIndex={0}
                    onClick={(): void => onUpdate(label)}
                    onKeyPress={(): boolean => false}
                >
                    <EditOutlined />
                </span>
            </CVATTooltip>
            <CVATTooltip title={t('Delete label')}>
                <span
                    style={{ color: textColor, cursor: 'pointer' }}
                    role='button'
                    tabIndex={0}
                    onClick={(): void => onDelete(label)}
                    onKeyPress={(): boolean => false}
                >
                    <DeleteOutlined />
                </span>
            </CVATTooltip>
        </div>
    );
}
